import type {
  ApiSubscriptionId,
  ApiTabId,
  ApiTestTypeId,
  FilterEnrollment,
  FilterStatus,
  TestTabFilterRequestBody
} from '@api/fetchers/goal/test-tab/types';
import { TEST_SERIES_FILTER } from '../Common/TestSeriesFilters';

import {
  API_TAB_ID,
  TAB_DEFAULT_FILTER_BODY,
  UI_TO_API_SUBSCRIPTION,
  UI_TO_API_TEST_TYPE,
  WEB_TAB_STATUS_OVERRIDE,
  getVisibleFiltersForCategoryTab
} from './constants';
import type {
  BuildFilterRequestBodyParams,
  FilterSelections,
  UiSubscriptionId,
  UiTestTypeId
} from './types';

const mapSubscriptionToApi = (
  selectedUiIds: string[] | undefined,
  defaultApiIds: ApiSubscriptionId[]
): ApiSubscriptionId[] => {
  if (!selectedUiIds?.length) {
    return defaultApiIds;
  }

  return selectedUiIds
    .map((id) => UI_TO_API_SUBSCRIPTION[id as UiSubscriptionId])
    .filter(Boolean) as ApiSubscriptionId[];
};

const mapTestTypeToApi = (
  selectedUiIds: string[] | undefined,
  defaultApiIds: ApiTestTypeId[]
): ApiTestTypeId[] => {
  if (!selectedUiIds?.length) {
    return defaultApiIds;
  }

  return selectedUiIds
    .map((id) => UI_TO_API_TEST_TYPE[id as UiTestTypeId])
    .filter(Boolean) as ApiTestTypeId[];
};

const mapEducatorsToApi = (selectedIds: string[] | undefined): string[] =>
  selectedIds || [];

const mapStatusToApi = (
  selectedIds: string[] | undefined,
  defaultApiIds: FilterStatus[]
): FilterStatus[] => {
  if (!selectedIds?.length) {
    return defaultApiIds;
  }

  return selectedIds as FilterStatus[];
};

const mapEnrollmentToApi = (
  selectedIds: string[] | undefined,
  defaultApiIds: FilterEnrollment[]
): FilterEnrollment[] => {
  if (!selectedIds?.length) {
    return defaultApiIds;
  }

  return selectedIds as FilterEnrollment[];
};

export const buildFilterRequestBody = ({
  apiTabId,
  categoryTabId,
  appliedSelections = {},
  isLoggedIn = false
}: BuildFilterRequestBodyParams): TestTabFilterRequestBody => {
  const defaults =
    TAB_DEFAULT_FILTER_BODY[apiTabId] ||
    TAB_DEFAULT_FILTER_BODY[API_TAB_ID.UPCOMING];
  const statusOverride =
    WEB_TAB_STATUS_OVERRIDE[
      categoryTabId as keyof typeof WEB_TAB_STATUS_OVERRIDE
    ];
  const selections = appliedSelections as FilterSelections;
  const visibleFilters = getVisibleFiltersForCategoryTab(categoryTabId).filter(
    (filterId) => isLoggedIn || filterId !== 'enrollment'
  );
  const showStatusFilter = visibleFilters.includes('status');
  const showTestTypeFilter = visibleFilters.includes('test_type');
  const showEnrollmentFilter = visibleFilters.includes('enrollment');

  const getEnrollmentForRequest = (): FilterEnrollment[] => {
    if (!isLoggedIn && apiTabId === API_TAB_ID.TEST_SERIES) {
      return [];
    }

    if (showEnrollmentFilter) {
      return mapEnrollmentToApi(
        selections[TEST_SERIES_FILTER.ENROLLMENT],
        defaults.enrollment
      );
    }

    return defaults.enrollment;
  };

  return {
    schedule: [],
    educators: mapEducatorsToApi(selections[TEST_SERIES_FILTER.EDUCATOR]),
    subscription: mapSubscriptionToApi(
      selections[TEST_SERIES_FILTER.SUBSCRIPTION],
      defaults.subscription
    ),
    test_type: showTestTypeFilter
      ? mapTestTypeToApi(
          selections[TEST_SERIES_FILTER.TEST_TYPE],
          defaults.test_type
        )
      : defaults.test_type,
    status: showStatusFilter
      ? mapStatusToApi(selections[TEST_SERIES_FILTER.STATUS], defaults.status)
      : statusOverride || defaults.status,
    enrollment: getEnrollmentForRequest()
  };
};

export type { ApiTabId };
